import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';

type StorageValue = string | null;

const isNativePlatform = (): boolean => Capacitor.isNativePlatform();

const isMissingStorageKeyError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  return error.message.includes('Item with given key does not exist');
};

/**
 * Servicio centralizado de almacenamiento.
 * - Usa SecureStorage para datos sensibles (tokens) en plataformas nativas
 * - Usa Preferences para datos no sensibles
 * - Fallback a Preferences en web (SecureStorage no soportado)
 */
export const StorageService = {
  /**
   * Obtiene un valor del almacenamiento seguro (para tokens)
   */
  async getSecure(key: string): Promise<StorageValue> {
    try {
      if (isNativePlatform()) {
        const { value } = await SecureStoragePlugin.get({ key });
        return value ?? null;
      }
      // Fallback a Preferences en web
      const { value } = await Preferences.get({ key });
      return value ?? null;
    } catch (error) {
      // Si la key no existe en SecureStorage, lanza error
      // Intentamos con Preferences como fallback (migración)
      try {
        const { value } = await Preferences.get({ key });
        if (value && isNativePlatform()) {
          // Migrar a almacenamiento seguro
          await this.setSecure(key, value);
          await Preferences.remove({ key });
        }
        return value ?? null;
      } catch {
        if (!isMissingStorageKeyError(error)) {
          console.error(`[StorageService] Error getting secure key "${key}"`, error);
        }
        return null;
      }
    }
  },

  /**
   * Guarda un valor en el almacenamiento seguro (para tokens)
   */
  async setSecure(key: string, value: string): Promise<void> {
    try {
      if (isNativePlatform()) {
        await SecureStoragePlugin.set({ key, value });
      } else {
        // Fallback a Preferences en web
        await Preferences.set({ key, value });
      }
    } catch (error) {
      console.error(`[StorageService] Error setting secure key "${key}"`, error);
      // Fallback a Preferences si SecureStorage falla
      await Preferences.set({ key, value });
    }
  },

  /**
   * Elimina un valor del almacenamiento seguro
   */
  async removeSecure(key: string): Promise<void> {
    try {
      if (isNativePlatform()) {
        await SecureStoragePlugin.remove({ key });
      }
      // También eliminar de Preferences por si existe ahí (migración)
      await Preferences.remove({ key });
    } catch (error) {
      console.error(`[StorageService] Error removing secure key "${key}"`, error);
    }
  },

  /**
   * Obtiene un valor del almacenamiento regular (para preferencias)
   */
  async get(key: string): Promise<StorageValue> {
    try {
      const { value } = await Preferences.get({ key });
      return value ?? null;
    } catch (error) {
      if (!isMissingStorageKeyError(error)) {
        console.error(`[StorageService] Error getting key "${key}"`, error);
      }
      return null;
    }
  },

  /**
   * Guarda un valor en el almacenamiento regular (para preferencias)
   */
  async set(key: string, value: string): Promise<void> {
    try {
      await Preferences.set({ key, value });
    } catch (error) {
      console.error(`[StorageService] Error setting key "${key}"`, error);
    }
  },

  /**
   * Elimina un valor del almacenamiento regular
   */
  async remove(key: string): Promise<void> {
    try {
      await Preferences.remove({ key });
    } catch (error) {
      console.error(`[StorageService] Error removing key "${key}"`, error);
    }
  },

  /**
   * Limpia todo el almacenamiento regular (no afecta SecureStorage)
   */
  async clearAll(): Promise<void> {
    try {
      await Preferences.clear();
    } catch (error) {
      console.error('[StorageService] Error clearing storage', error);
    }
  },

  /**
   * Limpia las keys de autenticación del almacenamiento seguro
   */
  async clearAuthKeys(keys: string[]): Promise<void> {
    await Promise.all(keys.map((key) => this.removeSecure(key)));
  },
};
